import { z } from 'zod';

/**
 * Retail Media (Etappe RM-1): Werbekonten, Prepaid-Guthaben und Aufladung über Stripe oder Mollie.
 *
 * Ein Werbekonto gehört zu genau einem Marktplatz. Im Marktplatz bucht ein Händler mit seinem bestehenden Konto,
 * in einem Shop ohne Marktplatz-Modul eine eingeladene Marke (RM-8). Das Guthaben ist der Budgetdeckel: Jeder
 * abgerechnete Klick bucht dagegen, ohne Guthaben keine Teilnahme an der Auktion (ab RM-3).
 *
 * Zahlungsempfänger je Marktplatz: der Marktplatz selbst (sein Mollie-Konto) oder später DXL zentral. Jede
 * Aufladung und jede Buchung hält fest, an wen das Geld ging – ein Wechsel ändert nichts an bestehendem Guthaben.
 */

export const WERBEKONTO_TYPEN = { haendler: 'Händler', marke: 'Marke' } as const;
export type WerbekontoTyp = keyof typeof WERBEKONTO_TYPEN;

export const WERBEKONTO_STATUS = { aktiv: 'Aktiv', gesperrt: 'Gesperrt' } as const;
export type WerbekontoStatus = keyof typeof WERBEKONTO_STATUS;

export const GUTHABEN_ARTEN = {
  aufladung: 'Aufladung',
  klick: 'Klickabzug',
  korrektur: 'Korrektur',
  erstattung: 'Erstattung',
  /** RM-9: Abbuchung einer WKZ-Buchung (Festpreis) */
  wkz: 'WKZ-Buchung',
  /** Abbuchung offener Kosten vom hinterlegten Zahlungsmittel (Migration 0580) */
  abbuchung: 'Abbuchung',
} as const;
export type GuthabenArt = keyof typeof GUTHABEN_ARTEN;

export const ZAHLUNGSEMPFAENGER = { marktplatz: 'Marktplatz', dxl: 'DXL' } as const;
export type Zahlungsempfaenger = keyof typeof ZAHLUNGSEMPFAENGER;

export const AUFLADUNG_STATUS = {
  offen: 'Offen',
  bezahlt: 'Bezahlt',
  abgebrochen: 'Abgebrochen',
  abgelaufen: 'Abgelaufen',
  fehlgeschlagen: 'Fehlgeschlagen',
} as const;
export type AufladungStatus = keyof typeof AUFLADUNG_STATUS;

/** Grenzen einer Aufladung in Cent: 10 € bis 10.000 € */
export const AUFLADUNG_MIN_CENT = 1_000;
export const AUFLADUNG_MAX_CENT = 1_000_000;
/** Größte Korrektur in Cent (in beide Richtungen) */
export const KORREKTUR_MAX_CENT = 10_000_000;

/** Platzierungsschlüssel wie „kategorie_grid“ – Stammdaten je Marktplatz, ab RM-2 gepflegt */
export const PLATZIERUNG_SCHLUESSEL = /^[a-z][a-z0-9_]{1,39}$/;

/* ------------------------------------------------------------------ Eingaben */

const Name = z.string().trim().min(2, 'Bitte Namen angeben').max(120, 'Höchstens 120 Zeichen');
const Cent = z.number({ invalid_type_error: 'Bitte Betrag angeben' }).int('Bitte Betrag in Cent angeben');

/** Händler legt sein Werbekonto im Portal an (Name vorbelegt mit der Firma) */
export const WerbekontoAnlegenSchema = z.object({ name: Name });
export type WerbekontoAnlegen = z.infer<typeof WerbekontoAnlegenSchema>;

/** Zahlungswege für Aufladungen – über den eigenen Zugang des Marktplatzes (Einstellungen → Zahlungsanbieter) */
export const AUFLADUNG_ANBIETER = { stripe: 'Stripe', mollie: 'Mollie' } as const;
export type AufladungAnbieter = keyof typeof AUFLADUNG_ANBIETER;
export const AUFLADUNG_ANBIETER_LISTE = Object.keys(AUFLADUNG_ANBIETER) as [AufladungAnbieter, ...AufladungAnbieter[]];

export const AufladungStartenSchema = z.object({
  cent: Cent
    .min(AUFLADUNG_MIN_CENT, `Mindestens ${AUFLADUNG_MIN_CENT / 100} €`)
    .max(AUFLADUNG_MAX_CENT, `Höchstens ${(AUFLADUNG_MAX_CENT / 100).toLocaleString('de-DE')} €`),
  /** ohne Angabe: der beim Marktplatz eingestellte Anbieter, sonst der erste eingerichtete */
  anbieter: z.enum(AUFLADUNG_ANBIETER_LISTE).optional(),
});
export type AufladungStarten = z.infer<typeof AufladungStartenSchema>;

/** Korrektur durch den Marktplatz: positiv schreibt gut, negativ belastet – nie unter das verfügbare Guthaben */
export const GuthabenKorrekturSchema = z.object({
  cent: Cent.refine((c) => c !== 0, 'Betrag darf nicht 0 sein').refine((c) => Math.abs(c) <= KORREKTUR_MAX_CENT, 'Betrag zu hoch'),
  grund: z.string().trim().min(3, 'Bitte Grund angeben').max(300, 'Höchstens 300 Zeichen'),
});
export type GuthabenKorrektur = z.infer<typeof GuthabenKorrekturSchema>;

export const WerbekontoStatusSchema = z.object({
  status: z.enum(['aktiv', 'gesperrt']),
  grund: z.string().trim().max(300, 'Höchstens 300 Zeichen').default(''),
});
export type WerbekontoStatusAendern = z.infer<typeof WerbekontoStatusSchema>;

export const WerbekontenAbfrage = z.object({
  cursor: z.string().max(400).optional(),
  suche: z.string().trim().max(120).optional(),
  status: z.enum(['aktiv', 'gesperrt']).optional(),
  typ: z.enum(['haendler', 'marke']).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});
export type WerbekontenFilter = z.infer<typeof WerbekontenAbfrage>;

export const GuthabenBuchungenAbfrage = z.object({
  cursor: z.string().max(400).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});

/** Marktplatz legt für einen seiner Händler ein Werbekonto an (Name leer: Firma des Händlers) */
export const KontoWerbekontoAnlegenSchema = z.object({
  haendlerId: z.string().uuid('Bitte Händler auswählen'),
  name: z.union([Name, z.literal('')]).optional(),
});
export type KontoWerbekontoAnlegen = z.infer<typeof KontoWerbekontoAnlegenSchema>;

/* ------------------------------------------------------------------ Antworten */

export interface GuthabenStand {
  saldoCent: number;
  reserviertCent: number;
  /** wirksamer Kreditrahmen: nur mit gültigem Zahlungsmittel und ohne fehlgeschlagene Abbuchung, sonst 0 */
  kreditCent: number;
  /** saldo − reserviert + kredit: was für neue Klicks bereitsteht */
  verfuegbarCent: number;
}

export interface Werbekonto {
  id: string;
  typ: WerbekontoTyp;
  haendlerId: string | null;
  name: string;
  status: WerbekontoStatus;
  statusGrund: string;
  guthaben: GuthabenStand;
  erstelltAm: string;
}

export interface WerbekontoZeile extends Werbekonto {
  /** Firma des Händlers (bei Händlerkonten) */
  haendler: string | null;
}

export interface WerbekontoSeite {
  eintraege: WerbekontoZeile[];
  weiter: string | null;
}

export interface GuthabenBuchung {
  id: string;
  art: GuthabenArt;
  betragCent: number;
  saldoNachCent: number;
  empfaenger: Zahlungsempfaenger;
  text: string;
  referenz: string | null;
  zeitpunkt: string;
}

export interface GuthabenBuchungSeite {
  eintraege: GuthabenBuchung[];
  weiter: string | null;
}

export interface Aufladung {
  id: string;
  cent: number;
  status: AufladungStatus;
  anbieter: AufladungAnbieter;
  empfaenger: Zahlungsempfaenger;
  testmodus: boolean;
  erstelltAm: string;
  bezahltAm: string | null;
}

/** Antwort beim Start: Weiterleitung zum Bezahlen bei Stripe bzw. Mollie */
export interface AufladungWeiterleitung {
  aufladung: Aufladung;
  url: string;
}

/** Werbung aus Sicht des Händlerportals */
export interface PortalWerbung {
  /** Modul Retail Media für diesen Marktplatz zugeschaltet */
  verfuegbar: boolean;
  /** Aufladen möglich (Zahlungsweg eingerichtet) – sonst Grund für die Oberfläche */
  aufladenMoeglich: boolean;
  aufladenHinweis: string;
  /** eingerichtete Zahlungswege, der bevorzugte zuerst */
  aufladenAnbieter: AufladungAnbieter[];
  konto: Werbekonto | null;
  /** letzte Aufladungen (höchstens 20) */
  aufladungen: Aufladung[];
  darfVerwalten: boolean;
}

/** Einstellungen Retail Media im Konto des Marktplatzes */
export interface WerbungEinstellungen {
  zahlungsempfaenger: Zahlungsempfaenger;
  /** Mollie-Zugang des Marktplatzes hinterlegt (Einstellungen → Zahlungsanbieter) */
  mollieEingerichtet: boolean;
  mollieTest: boolean;
  /** Stripe-Schlüssel des Marktplatzes hinterlegt – Aufladung per Stripe Checkout */
  stripeEingerichtet: boolean;
  stripeTest: boolean;
  /** Attributionsfenster für Klicks in Tagen (RM-4, 1–30, Standard 14) */
  attributionTage: number;
}

/* ------------------------------------------------------------------ Standalone (RM-8): Shop ohne Marktplatz, Marken */

/**
 * Name des Angebots für Shops ohne Marktplatz – noch nicht endgültig, deshalb nur hier. Oberfläche, E-Mails und
 * Vermarktungsseite lesen ihn von dieser Stelle.
 */
export const RETAIL_MEDIA_PRODUKT = 'DXL Retail Media';

/** Ein Mandant nur mit Retail Media (ohne Modul marktplatz) – ein Shop, der Marken auf seinen Seiten werben lässt */
export const nurWerbung = (module: readonly string[]) => module.includes('werbung') && !module.includes('marktplatz');

/** Höchstzahl Markennamen je Marken-Werbekonto (wie markenSetzen im Backend) */
export const MARKEN_MAX = 50;

const MarkenListe = z
  .array(z.string().trim().max(120, 'Höchstens 120 Zeichen je Marke'))
  .max(MARKEN_MAX, `Höchstens ${MARKEN_MAX} Marken`)
  .transform((l) => [...new Set(l.map((m) => m.trim()).filter(Boolean))])
  .refine((l) => l.length > 0, 'Bitte mindestens einen Markennamen angeben');

/** Shop lädt eine Marke ein: Händler-Datensatz der Art marke, Portal-Zugang (Inhaber) und Werbekonto vom Typ marke */
export const MarkeEinladenSchema = z.object({
  firma: z.string().trim().min(2, 'Bitte Firmennamen angeben').max(120, 'Höchstens 120 Zeichen'),
  ansprechpartner: z.string().trim().max(120, 'Höchstens 120 Zeichen').default(''),
  email: z.string().trim().toLowerCase().email('Bitte gültige E-Mail angeben').max(190),
  marken: MarkenListe,
});
export type MarkeEinladen = z.input<typeof MarkeEinladenSchema>;

export const MarkenSetzenSchema = z.object({ marken: MarkenListe });
export type MarkenSetzen = z.input<typeof MarkenSetzenSchema>;

/** offen = Link verschickt, noch kein Passwort; angenommen = Passwort gesetzt; abgelaufen = Link nicht mehr gültig */
export const MARKE_EINLADUNG = { offen: 'Eingeladen', angenommen: 'Zugang aktiv', abgelaufen: 'Einladung abgelaufen' } as const;
export type MarkeEinladungStand = keyof typeof MARKE_EINLADUNG;

export interface MarkeZeile {
  werbekontoId: string;
  haendlerId: string;
  firma: string;
  email: string;
  marken: string[];
  einladung: MarkeEinladungStand;
  letzteAnmeldung: string | null;
  status: WerbekontoStatus;
  guthaben: GuthabenStand;
  erstelltAm: string;
}

export interface MarkeSeite {
  eintraege: MarkeZeile[];
  weiter: string | null;
}

export interface MarkeEingeladen {
  marke: MarkeZeile;
  /** Einmal-Link zum Festlegen des Passworts – zum Weitergeben, falls die E-Mail nicht ankommt */
  link: string;
  versand: 'gesendet' | 'nicht_eingerichtet' | 'fehler';
}

export const MarkenAbfrage = z.object({
  cursor: z.string().max(400).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});

/** Produktsuche einer Marke im Portal: nur Produkte ihrer Markennamen, nur gelistet bzw. freigegeben */
export const MarkenProdukteAbfrage = z.object({
  q: z.string().trim().max(120).optional(),
  cursor: z.string().max(400).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(25),
});

export interface MarkenProdukt {
  id: string;
  titel: string;
  marke: string;
  bild: string | null;
  status: 'freigegeben' | 'gelistet';
  sku: string;
}

export interface MarkenProduktSeite {
  eintraege: MarkenProdukt[];
  weiter: string | null;
}
